import React, { useEffect, useState, useCallback, useMemo } from "react";
import debounce from "lodash/debounce";
import { httpService } from "../../../services/httpService";
import { toast } from "react-toastify";

// Reusable toggle section wrapper
const ToggleSection = React.memo(
  ({
    label,
    active,
    onToggle,
    expanded,
    onExpand,
    children,
    availableSizes,
    disabled = false,
  }) => {
    // 🔹 Handle expand (only if toggle ON)
    const handleExpand = (e) => {
      e.stopPropagation();
      if (disabled) return;
      if (active) onExpand?.();
    };

    // 🔹 Handle toggle (auto expand when ON, auto collapse when OFF)
    const handleToggle = (e) => {
      e.stopPropagation();
      if (disabled) return;
      onToggle?.();

      // If turning ON → expand
      if (!active) {
        onExpand?.();
      }
      // If turning OFF → collapse
      else {
        onExpand?.(false); // Pass explicit false
      }
    };
   
    return (
      <div className="grid grid-cols-1 border border-gray-200 shadow rounded-lg overflow-hidden">
        <div className="flex justify-between items-center bg-gray-50 p-2">
          <div className="flex items-center gap-4">
            <label>{label}</label>
            <button
              type="button"
              aria-pressed={active}
              onClick={handleToggle}
              disabled={disabled}
              className={`relative inline-flex items-center h-6 rounded-full w-14 transition-colors duration-300 ${
                active && !disabled ? "bg-green-500" : "bg-gray-300"
              } ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
            >
              <span
                className={`inline-block w-6 h-6 transform rounded-full bg-white shadow transition-transform duration-300 ${
                  active && !disabled ? "translate-x-8" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {/* 🔹 Expand icon only works when active */}
          <button
            onClick={handleExpand}
            className={`p-1 ${
              !active || disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={!active || disabled}
          >
            <svg
              className={`h-5 w-5 text-gray-500 transition-transform duration-300 ${
                expanded ? "rotate-180" : ""
              }`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* 🔹 Show children only if expanded & active */}
        {expanded && active && !disabled && (
          <div className="bg-white p-2">{children}</div>
        )}
      </div>
    );
  }
);

const PriceSurchargeForm = React.memo(
  ({
    isActive,
    data,
    updateData,
    onValidationChange,
    selectedPetTypes,
    sizeCoatSelection = "size_and_coat",
  }) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [hasValidationErrors, setHasValidationErrors] = useState(false);
    const [availableSizes, setAvailableSizes] = useState([]);

    const [form, setForm] = useState({
      ...data,
      sizeOptions: data.sizeOptions || {},
      coatOptions: data.coatOptions || {},
      multipleAppointmentDiscounts: data.multipleAppointmentDiscounts || [],
      clientLabelDiscounts: data.clientLabelDiscounts || [],
    });
    const [expandedSections, setExpandedSections] = useState({});
    const disableSizeSurcharge = sizeCoatSelection === "only_coat";
    const disableCoatSurcharge = sizeCoatSelection === "only_size";

    const toggleSection = useCallback((section, value) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: typeof value === "boolean" ? value : !prev[section],
      }));
    }, []);

    useEffect(() => {
      if (isActive) {
        setForm({
          ...data,
          sizeOptions: data.sizeOptions || {},
          coatOptions: data.coatOptions || {},
          multipleAppointmentDiscounts: data.multipleAppointmentDiscounts || [],
          clientLabelDiscounts: data.clientLabelDiscounts || [],
        });
      }
    }, [data, isActive]);

    useEffect(() => {
      setForm((prev) => {
        const next = { ...prev };
        let changed = false;

        if (disableSizeSurcharge && next.priceBasedOnSize !== "no") {
          next.priceBasedOnSize = "no";
          changed = true;
        }

        if (disableCoatSurcharge && next.priceBasedOnCoat !== "no") {
          next.priceBasedOnCoat = "no";
          changed = true;
        }

        return changed ? next : prev;
      });
    }, [disableSizeSurcharge, disableCoatSurcharge]);

    // Fetch pet types and get enabled sizes based on selected pet types
    useEffect(() => {
      const fetchPetTypes = async () => {
        try {

          const response = await httpService.get("/vendors/getPetTypes");
          const petTypesData = response.data;

          // If no pet types selected, show all enabled sizes
          if (!selectedPetTypes || selectedPetTypes.length === 0) {
            const enabledSizes = [];
            petTypesData.forEach((pet) => {
              if (pet.enabled) {
                pet.sizes
                  .filter((size) => size.enabled === true)
                  .forEach((size) => {
                    enabledSizes.push(size.size_label.toLowerCase());
                  });
              }
            });
            const uniqueSizes = [...new Set(enabledSizes)];
            const sizeOrder = ["small", "medium", "large", "giant"];
            const sortedSizes = sizeOrder.filter((size) =>
              uniqueSizes.includes(size),
            );
            setAvailableSizes(sortedSizes);
            return;
          }

          // Create a map of pet_type_id to pet name
          const petIdToNameMap = {};
          petTypesData.forEach((pet) => {
            petIdToNameMap[pet.pet_type_id] = pet.name?.toLowerCase();
          });

          // Convert selected pet IDs to names
          const selectedPetNames = selectedPetTypes
            .map((petId) => {
              // Handle both object and string/ID formats
              const id =
                typeof petId === "object"
                  ? petId.pet_type_id || petId._id
                  : petId;
              const name = petIdToNameMap[id];
              return name;
            })
            .filter((name) => name); // Remove undefined names

          if (selectedPetNames.length === 0) {
            setAvailableSizes([]);
            return;
          }

          const enabledSizes = [];
          petTypesData.forEach((pet) => {
            const petName = pet.name?.toLowerCase();

            if (selectedPetNames.includes(petName) && pet.enabled) {
              pet.sizes
                .filter((size) => size.enabled === true)
                .forEach((size) => {
                  enabledSizes.push(size.size_label.toLowerCase());
                });
            }
          });

          const uniqueSizes = [...new Set(enabledSizes)];
          const sizeOrder = ["small", "medium", "large", "giant"];
          const sortedSizes = sizeOrder.filter((size) =>
            uniqueSizes.includes(size),
          );
          setAvailableSizes(sortedSizes);
        } catch (error) {
          console.error("Failed to fetch pet types:", error);
          toast.error("Could not load pet size configurations");
        }
      };

      fetchPetTypes();
    }, [selectedPetTypes]);

    // 🔹 ADD THIS: Validation function for discount fields
    const validateDiscountSections = useCallback((formData) => {
      const errors = [];

      // Validate multiple appointment discounts
      if (formData.priceDiscountForMulitpleAppointment === "yes") {
        formData.multipleAppointmentDiscounts.forEach((item) => {
          if (item.discount && !item.discount_type) {
            errors.push(
              `Please select discount type for ${item.no_of_pet} appointments`,
            );
          }
        });
      }

      // Validate client label discounts
      if (formData.priceDiscountForClientLabel === "yes") {
        formData.clientLabelDiscounts.forEach((item) => {
          if (item.discount && !item.discount_type) {
            errors.push(`Please select discount type for ${item.label}`);
          }
        });
      }

      return errors;
    }, []);

    // 🔹 ADD THIS: Update validation when form changes
    useEffect(() => {
      const errors = validateDiscountSections(form);
      const hasErrors = errors.length > 0;
      setHasValidationErrors(hasErrors);

      // Pass validation status to parent
      onValidationChange?.(!hasErrors);
    }, [form, validateDiscountSections, onValidationChange]);

    // ... keep all your existing useEffect hooks for expanded sections
    useEffect(() => {
      if (form.priceBasedOnSize === "yes") {
        setExpandedSections((prev) => ({ ...prev, size: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, size: false }));
      }
    }, [form.priceBasedOnSize]);

    useEffect(() => {
      if (form.priceBasedOnCoat === "yes") {
        setExpandedSections((prev) => ({ ...prev, coat: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, coat: false }));
      }
    }, [form.priceBasedOnCoat]);

    useEffect(() => {
      if (form.priceDiscountForMulitpleAppointment === "yes") {
        setExpandedSections((prev) => ({ ...prev, multipleAppointment: true }));
      } else {
        setExpandedSections((prev) => ({
          ...prev,
          multipleAppointment: false,
        }));
      }
    }, [form.priceDiscountForMulitpleAppointment]);

    useEffect(() => {
      if (form.priceDiscountForClientLabel === "yes") {
        setExpandedSections((prev) => ({ ...prev, clientLabel: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, clientLabel: false }));
      }
    }, [form.priceDiscountForClientLabel]);

    // Remove the old validate function and replace with this simplified version
    const validate = useCallback(() => {
      // This can be empty now since we're handling validation in the useEffect above
    }, []);

    // Remove the debounced validation for name/category since it's not needed for this form
    const clampNonNegative = useCallback((val) => {
      const n = Number(val);
      return isNaN(n) || n < 0 ? "" : String(n);
    }, []);

    // Handlers
    const handleChange = useCallback((field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleSizePrice = useCallback(
      (size, val) => {
        setForm((prev) => ({
          ...prev,
          sizeOptions: { ...prev.sizeOptions, [size]: clampNonNegative(val) },
        }));
      },
      [clampNonNegative],
    );

    const handleCoatPrice = useCallback(
      (size, val) => {
        setForm((prev) => ({
          ...prev,
          coatOptions: { ...prev.coatOptions, [size]: clampNonNegative(val) },
        }));
      },
      [clampNonNegative],
    );

    const handleMultipleAppointmentChange = useCallback((num, type, val) => {
      setForm((prev) => {
        const list = [...prev.multipleAppointmentDiscounts];
        const idx = list.findIndex((i) => i.no_of_pet === num);
        const entry =
          idx > -1
            ? { ...list[idx] }
            : { no_of_pet: num, discount_type: "", discount: "" };
        entry.discount_type = type;
        entry.discount = val;
        if (idx > -1) list[idx] = entry;
        else list.push(entry);
        return { ...prev, multipleAppointmentDiscounts: list };
      });
    }, []);

    const handleClientLabelChange = useCallback((label, type, val) => {
      setForm((prev) => {
        const list = [...prev.clientLabelDiscounts];
        const idx = list.findIndex((i) => i.label === label);
        const entry =
          idx > -1
            ? { ...list[idx] }
            : { label: label, discount_type: "", discount: "" };
        entry.discount_type = type;
        entry.discount = val;
        if (idx > -1) list[idx] = entry;
        else list.push(entry);
        return { ...prev, clientLabelDiscounts: list };
      });
    }, []);

    // Debounced update to parent
    const debouncedUpdate = useMemo(
      () => debounce(updateData, 300),
      [updateData],
    );
    useEffect(() => {
      debouncedUpdate(form);
      return () => debouncedUpdate.cancel();
    }, [form, debouncedUpdate]);

    if (loading)
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      );

    const getAppointmentEntry = (num) =>
      form.multipleAppointmentDiscounts.find((o) => o.no_of_pet === num) || {};
    const clientLabels = [
      "VIP Client",
      "New Client",
      "Regular Client",
      "Premium Client",
      "Frequent Visitor",
    ];

    return (
      <div className="space-y-4">
        {/* Keep all your existing ToggleSection components exactly as they are */}
        {!disableSizeSurcharge && (
          <ToggleSection
            label="Set Price Surcharge based on size"
            active={form.priceBasedOnSize === "yes"}
            onToggle={() =>
              handleChange(
                "priceBasedOnSize",
                form.priceBasedOnSize === "yes" ? "no" : "yes",
              )
            }
            expanded={expandedSections.size}
            onExpand={() => toggleSection("size")}
            availableSizes={availableSizes}
          >
            {availableSizes.length > 0 ? (
              availableSizes.map((size) => (
                <div key={size} className="flex items-center gap-2 mb-2">
                  <label
                    htmlFor={`size-${size}`}
                    className="w-[10%] capitalize"
                  >
                    {size}
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      ₹
                    </span>
                    <input
                      id={`size-${size}`}
                      type="number"
                      min={0}
                      placeholder="Enter price"
                      className="block pl-8 pr-2 py-2 border border-gray-300 rounded"
                      value={form.sizeOptions[size] || ""}
                      onChange={(e) => handleSizePrice(size, e.target.value)}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">
                Loading size options...
              </div>
            )}
          </ToggleSection>
        )}

        {/* Coat toggle – hidden when only_size */}
        {!disableCoatSurcharge && (
          <ToggleSection
            label="Set Price Surcharge based on Coat"
            active={form.priceBasedOnCoat === "yes"}
            onToggle={() =>
              handleChange(
                "priceBasedOnCoat",
                form.priceBasedOnCoat === "yes" ? "no" : "yes",
              )
            }
            expanded={expandedSections.coat}
            onExpand={() => toggleSection("coat")}
          >
            {["short", "medium", "long"].map((size) => (
              <div key={size} className="flex items-center gap-2 mb-2">
                <label
                  htmlFor={`coatsize-${size}`}
                  className="w-[10%] capitalize"
                >
                  {size}
                </label>
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    ₹
                  </span>
                  <input
                    id={`coatsize-${size}`}
                    type="number"
                    min={0}
                    placeholder="Enter price"
                    className="block pl-8 pr-2 py-2 border border-gray-300 rounded"
                    value={form.coatOptions[size] || ""}
                    onChange={(e) => handleCoatPrice(size, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </ToggleSection>
        )}
        {/*  
        <ToggleSection
          label="Set Price Discount for multiple Appointments"
          active={form.priceDiscountForMulitpleAppointment === "yes"}
          onToggle={() =>
            handleChange(
              "priceDiscountForMulitpleAppointment",
              form.priceDiscountForMulitpleAppointment === "yes" ? "no" : "yes",
            )
          }
          expanded={expandedSections.multipleAppointment}
          onExpand={() => toggleSection("multipleAppointment")}
        >
          <div className="flex flex-col gap-2">
            {["2", "3-4", "5+"].map((num) => {
              const ent = getAppointmentEntry(num);
              return (
                <div key={num} className="flex items-center gap-2 mb-2">
                  <div className="w-[18%] capitalize">{num} Appointments</div>
                  <select
                    className="w-[22%] p-2 border border-gray-300 rounded"
                    value={ent.discount_type || ""}
                    onChange={(e) =>
                      handleMultipleAppointmentChange(
                        num,
                        e.target.value,
                        ent.discount,
                      )
                    }
                  >
                    <option value="">Discount Type</option>
                    <option value="value">Value</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    placeholder="Discount"
                    className="w-[15%] p-2 border border-gray-300 rounded"
                    value={ent.discount || ""}
                    onChange={(e) =>
                      handleMultipleAppointmentChange(
                        num,
                        ent.discount_type,
                        e.target.value,
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </ToggleSection>
        
        <ToggleSection
          label="Set Price Discount Based On Client Label"
          active={form.priceDiscountForClientLabel === "yes"}
          onToggle={() =>
            handleChange(
              "priceDiscountForClientLabel",
              form.priceDiscountForClientLabel === "yes" ? "no" : "yes",
            )
          }
          expanded={expandedSections.clientLabel}
          onExpand={() => toggleSection("clientLabel")}
        >
          <div className="flex flex-col gap-2">
            {clientLabels.map((label) => {
              const ent =
                form.clientLabelDiscounts.find((i) => i.label === label) || {};
              return (
                <div key={label} className="flex items-center gap-2 mb-2">
                  <div className="mr-2">{label}</div>
                  <select
                    className="w-[22%] p-2 border border-gray-300 rounded"
                    value={ent.discount_type || ""}
                    onChange={(e) =>
                      handleClientLabelChange(
                        label,
                        e.target.value,
                        ent.discount,
                      )
                    }
                  >
                    <option value="">Discount Type</option>
                    <option value="value">Value</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    placeholder="Discount"
                    className="w-[15%] p-2 border border-gray-300 rounded"
                    value={ent.discount || ""}
                    onChange={(e) =>
                      handleClientLabelChange(
                        label,
                        ent.discount_type,
                        e.target.value,
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </ToggleSection>*/}
      </div>
    );
  }
);

export default PriceSurchargeForm;
